import { ref, computed, watch } from 'vue'
import Swal from 'sweetalert2'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { DEFAULT_STOREFRONT_TEMPLATE_CATEGORIES } from '@/constants/storefrontTemplateCategoriesDefault.js'
import { getStorefrontThemePresetsForTemplate } from '@/constants/storefrontThemePresetsByTemplate.js'
import { readAppearance } from '../../../../shared/commerceAppearance.js'
import { showConfirm, showInfo } from '@/utils/swal.js'

export { DEFAULT_STOREFRONT_TEMPLATE_CATEGORIES } from '@/constants/storefrontTemplateCategoriesDefault.js'

const IMPLEMENTED_STOREFRONT_TEMPLATE_IDS = new Set([
  'standard',
  'retail',
  'showcase',
  'bakery',
  'style_modern',
])

export const SETTLEMENT_MODE_OPTIONS = [
  { value: 'online_gateway', label: '線上金流', desc: '信用卡、超商代碼等線上付款' },
  { value: 'bank_transfer', label: '銀行匯款', desc: '會員匯款後由人工確認' },
  { value: 'monthly_statement', label: '月結帳單', desc: '適合 B2B 企業客戶掛帳' },
  { value: 'none', label: '無需付款', desc: '全額點數兌換或免費商品' },
]

/** 啟用銀行匯款時，儲存商城設定前須填寫（與後台表單必填欄位一致） */
export const BANK_TRANSFER_SAVE_REQUIRED_FIELDS = [
  { key: 'bank_name', label: '銀行名稱' },
  { key: 'account_name', label: '戶名' },
  { key: 'account_number', label: '帳號' },
]

/** 各 Tab／整合區塊對應可局部 PATCH 的 settings 欄位（top-level key 整段取代） */
export const COMMERCE_SETTINGS_SCOPE_KEYS = {
  general: [
    'storefrontDisplayName',
    'storefrontLogoUrl',
    'storefrontHostname',
    'storefrontPublicBaseUrl',
    'storefrontMessagingChannelId',
    'commerceMode',
    'settlementModes',
    'orderCaptureOnly',
    'bankTransfer',
    'checkoutRequiresLogin',
    'ecomWarehouseId',
    'allowPointsCash',
    'allowFullPointsOnly',
  ],
  menu: ['menuItems'],
  footer: ['footerColumns', 'footerLegalText', 'footerConfig'],
  home: ['homepageConfig'],
  delivery: ['deliveryTimeConfig'],
  scheduled: ['scheduledFulfillment'],
  analytics: ['gtmContainerId', 'ga4MeasurementId', 'analyticsConsentRequired'],
  integration_invoice: ['invoiceOptions'],
  /** 金流／物流無 settings 欄位；dirty／儲存改走 integration draft */
  integration_payment: [],
  integration_logistics: [],
}

const INTEGRATION_SECTION_LABELS = {
  payment: '金流',
  invoice: '電子發票',
  logistics: '物流平台',
}

const LEGACY_INVOICE_FIELDS_NEVER_IN_PATCH = [
  'storefrontTemplateCategories',
  'invoiceProvider',
  'invoiceEnabled',
  'invoiceMerchantId',
  'invoiceHashKey',
  'invoiceHashIv',
  'invoiceIsSandbox',
  'invoiceAutoIssue',
]

/**
 * @param {string[]|undefined} settlementModes
 * @param {Record<string, unknown>|undefined} bankTransfer
 * @returns {string} 錯誤訊息；通過時為空字串
 */
export function getBankTransferSaveValidationMessage(settlementModes, bankTransfer) {
  if (!Array.isArray(settlementModes) || !settlementModes.includes('bank_transfer')) return ''
  const bt =
    bankTransfer && typeof bankTransfer === 'object' && !Array.isArray(bankTransfer)
      ? bankTransfer
      : {}
  const missing = []
  for (const { key, label } of BANK_TRANSFER_SAVE_REQUIRED_FIELDS) {
    if (!String(bt[key] ?? '').trim()) missing.push(label)
  }
  if (!missing.length) return ''
  return `已啟用「銀行匯款」，請填寫必填欄位後再儲存（未完成：${missing.join('、')}）。`
}

const PROVIDER_DISPLAY_NAMES = {
  payment: {
    ecpay: '綠界科技',
    newebpay: '藍新金流',
    tappay: '全方位支付',
    stripe: 'Stripe',
    ddpay: '點點付',
  },
  logistics: {
    ecpay: '綠界科技',
  },
  invoice: {
    ecpay: '綠界科技',
    wixtar: '星益欣',
  },
}

export function isCatalogIntegrationProvider(channel, providerCode) {
  const code = String(providerCode ?? '').trim().toLowerCase()
  const names = PROVIDER_DISPLAY_NAMES[channel] || {}
  return Object.prototype.hasOwnProperty.call(names, code)
}

export function integrationProviderRowLabel(channel, providerCode, displayName) {
  const code = String(providerCode ?? '').trim()
  const catalogName = PROVIDER_DISPLAY_NAMES[channel]?.[code.toLowerCase()]
  if (catalogName) return `${catalogName} (${code.toLowerCase()})`
  const customName = String(displayName ?? '').trim()
  if (customName) return `${customName} (${code})`
  return code
}

/** 儲存摘要 chip：短中文名；自訂用 display_name，否則 code */
export function integrationProviderChipLabel(channel, providerCode, displayName) {
  const code = String(providerCode ?? '').trim()
  const catalogName = PROVIDER_DISPLAY_NAMES[channel]?.[code.toLowerCase()]
  if (catalogName) return catalogName
  const customName = String(displayName ?? '').trim()
  if (customName) return customName
  return code || '未知服務'
}

function escapeIntegrationHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function integrationSaveChipHtml(label, failed = false) {
  const bg = failed ? '#fee2e2' : '#dcfce7'
  const color = failed ? '#991b1b' : '#166534'
  return `<span style="display:inline-block;background:${bg};color:${color};border-radius:999px;padding:4px 10px;font-size:12px;font-weight:600;margin:2px;">${escapeIntegrationHtml(label)}</span>`
}

/**
 * 金流／電子發票／物流區塊儲存結果（D／D2）：區塊主句 + chip；失敗時附原因。
 * @param {{ channel: string, successes: Array<{label:string}>, failures: Array<{label:string,error:string}> }} opts
 */
export function buildIntegrationSaveResultHtml({ channel, successes, failures }) {
  const section = INTEGRATION_SECTION_LABELS[channel] || channel
  let body = `${section}設定已儲存`
  if (failures.length && !successes.length) body = `${section}設定儲存失敗`
  else if (failures.length) body = `${section}設定部分已儲存`

  const chips = [
    ...successes.map((item) => integrationSaveChipHtml(item.label, false)),
    ...failures.map((item) => integrationSaveChipHtml(item.label, true)),
  ].join('')

  const reasonHtml = failures.length
    ? `<p style="margin:12px 0 0;font-size:14px;color:#6b7280;text-align:left;line-height:1.6;"><strong style="color:#2c3340;">失敗原因</strong><br/>${failures
        .map((item) => `${escapeIntegrationHtml(item.label)}：${escapeIntegrationHtml(item.error || '失敗')}`)
        .join('<br/>')}</p>`
    : ''

  return `<p style="margin:0 0 14px;font-size:14px;color:#6b7280;">${escapeIntegrationHtml(body)}</p><div style="display:flex;flex-wrap:wrap;gap:4px;justify-content:center;margin:0 0 4px;">${chips}</div>${reasonHtml}`
}

/** 儲存前清掉已廢棄、執行期不再使用的 credentials 鍵 */
function sanitizeIntegrationCredentials(channel, providerCode, credentials) {
  const next = { ...(credentials && typeof credentials === 'object' ? credentials : {}) }
  const code = String(providerCode ?? '').toLowerCase()
  if (channel === 'payment' && code === 'ddpay') {
    delete next.apiBaseUrl
  }
  if (channel === 'invoice' && code === 'wixtar') {
    delete next.endpointUrl
  }
  return next
}

export const PROVIDER_PRESET_SELECT_OPTIONS = [
  { value: 'ecpay', label: '綠界科技 (ecpay)' },
  { value: 'newebpay', label: '藍新金流 (newebpay)' },
  { value: 'tappay', label: '全方位支付 (tappay)' },
  { value: 'stripe', label: 'Stripe (stripe)' },
  { value: 'ddpay', label: '點點付 (ddpay)' },
  { value: '__custom__', label: '自訂（手動輸入代碼）' },
]

export const LOGISTICS_PROVIDER_PRESET_SELECT_OPTIONS = [
  { value: 'ecpay', label: '綠界科技 (ecpay)' },
  { value: '__custom__', label: '自訂（手動輸入代碼）' },
]

export const INVOICE_PROVIDER_PRESET_SELECT_OPTIONS = [
  { value: 'ecpay', label: '綠界科技 (ecpay)' },
  { value: 'wixtar', label: '星益欣 (wixtar)' },
]

const PROVIDER_FIELDS = {
  ecpay: [
    { key: 'MerchantID', label: '特店編號 (MerchantID)', required: true, secret: false, placeholder: '2000132' },
    { key: 'HashKey', label: 'HashKey', required: true, secret: true, placeholder: '5294y06JbISpM5x9' },
    { key: 'HashIV', label: 'HashIV', required: true, secret: true, placeholder: 'v77hoKGq4kWxNNIS' },
    {
      key: 'ReturnURL',
      label: '付款完成通知網址 (ReturnURL)',
      required: false,
      secret: false,
      hint:
        '正式／測試環境若已設定 API_BASE_URL，可留空由後端自動帶入。本機或綠界無法連內網時請填公開網址，結尾路徑須為 /api/v1/webhooks/ecpay/commerce-return（可用 ngrok 轉發至本機 API）',
    },
  ],
  newebpay: [
    { key: 'MerchantID', label: '商店代號 (MerchantID)', required: true, secret: false },
    { key: 'HashKey', label: 'HashKey', required: true, secret: true },
    { key: 'HashIV', label: 'HashIV', required: true, secret: true },
  ],
  tappay: [
    { key: 'PartnerKey', label: 'Partner Key', required: true, secret: true },
    { key: 'MerchantID', label: 'Merchant ID', required: true, secret: false },
  ],
  stripe: [
    { key: 'SecretKey', label: 'Secret Key (sk_...)', required: true, secret: true },
    {
      key: 'WebhookSecret',
      label: 'Webhook Signing Secret',
      required: false,
      secret: true,
      hint: '用於驗證 Stripe Webhook 事件真偽',
    },
  ],
  ddpay: [
    {
      key: 'clientId',
      label: 'Client ID',
      required: true,
      secret: false,
      placeholder: '例：1700000002',
      hint: 'POST /auth/token 請求體欄位；並作為後續 API 之 OAuth client 識別',
    },
    {
      key: 'clientSecret',
      label: 'Client Secret',
      required: true,
      secret: true,
      hint: '用於 token 簽章（clientId:apiVersion:ts）與 X-DD-Signature（body:ts:apiPath）；勿外流',
    },
    {
      key: 'shopId',
      label: 'Shop ID（店家 ID）',
      required: true,
      secret: false,
      placeholder: '例：D_1LJd7kjE4',
      hint: '建立訂單 POST …/orders 必填之 shopId（見 Postman「建立訂單」）',
    },
  ],
}

const INVOICE_PROVIDER_FIELDS = {
  ecpay: [
    {
      key: 'MerchantID',
      label: '特店編號 (InvoiceMerchantID)',
      required: true,
      secret: false,
      placeholder: '例如：2000132',
      hint: '綠界科技後台的特店編號，可能與金流特店編號不同。',
    },
    { key: 'HashKey', label: 'HashKey', required: true, secret: true, placeholder: '電子發票 HashKey' },
    { key: 'HashIV', label: 'HashIV', required: true, secret: true, placeholder: '電子發票 HashIV' },
  ],
  wixtar: [
    { key: 'businessid', label: '統一編號', required: true, secret: false },
    { key: 'branchno', label: '門市店碼', required: true, secret: false },
    { key: 'posno', label: 'POS 機碼', required: true, secret: false },
    { key: 'pid', label: 'POS 機序號', required: true, secret: false },
    { key: 'rid', label: 'RID', required: true, secret: false },
    { key: 'pname', label: 'POS 軟體名稱', required: true, secret: false },
  ],
}

const LOGISTICS_PROVIDER_FIELDS = {
  ecpay: [
    { key: 'MerchantID', label: '特店編號 (MerchantID)', required: true, secret: false },
    { key: 'HashKey', label: 'HashKey', required: true, secret: true },
    { key: 'HashIV', label: 'HashIV', required: true, secret: true },
    { key: 'SenderName', label: '寄件人姓名', required: true, secret: false },
    { key: 'SenderZipCode', label: '寄件人郵遞區號', required: true, secret: false },
    { key: 'SenderAddress', label: '寄件人地址', required: true, secret: false },
  ]
}

const PROVIDER_FIELDS_BY_CHANNEL = {
  payment: PROVIDER_FIELDS,
  invoice: INVOICE_PROVIDER_FIELDS,
  logistics: LOGISTICS_PROVIDER_FIELDS,
}

const ECPAY_OFFICIAL_SANDBOX_CREDENTIALS = {
  MerchantID: '2000132',
  HashKey: '5294y06JbISpM5x9',
  HashIV: 'v77hoKGq4kWxNNIS',
}

const ECPAY_LOGISTICS_B2C_SANDBOX_CREDENTIALS = {
  MerchantID: '2000132',
  HashKey: '5294y06JbISpM5x9',
  HashIV: 'v77hoKGq4kWxNNIS',
}

const ECPAY_LOGISTICS_C2C_SANDBOX_CREDENTIALS = {
  MerchantID: '2000933',
  HashKey: 'XBERn1YOvpM9nfZc',
  HashIV: 'h1ONHk4P4yqbl5LK',
}

const ECPAY_LOGISTICS_SANDBOX_SENDER = {
  SenderName: '陳大明',
  SenderZipCode: '11560',
  SenderAddress: '台北市南港區三重路19-2號6樓',
}

/** 綠界電子發票 Stage 測試特店（與系統設定頁／官方文件一致；與金流沙盒 Hash 不同） */
const ECPAY_OFFICIAL_SANDBOX_INVOICE = {
  invoiceMerchantId: '2000132',
  invoiceHashKey: 'ejCk326UnaZWKisg',
  invoiceHashIv: 'q9jcZX8Ib9LM8wYk',
}

function coerceJsonArrayFromApi(v, fallback = []) {
  if (v == null) return fallback
  if (Array.isArray(v)) return v
  if (typeof v === 'string') {
    const t = v.trim()
    if (!t) return fallback
    try {
      const p = JSON.parse(t)
      return Array.isArray(p) ? p : fallback
    } catch {
      return fallback
    }
  }
  return fallback
}

function coerceJsonObjectFromApi(v, fallback = {}) {
  if (v == null) return fallback
  if (typeof v === 'object' && !Array.isArray(v)) return v
  if (typeof v === 'string') {
    const t = v.trim()
    if (!t) return fallback
    try {
      const p = JSON.parse(t)
      return p && typeof p === 'object' && !Array.isArray(p) ? p : fallback
    } catch {
      return fallback
    }
  }
  return fallback
}

function homepageSettingsFromApi(raw) {
  const value = coerceJsonObjectFromApi(raw, {})
  return {
    ...value,
    announcement: { enabled: true, text: '', href: '', ...coerceJsonObjectFromApi(value.announcement, {}) },
    sections: coerceJsonArrayFromApi(value.sections, []).map(section => ({
      ...section,
      productIds: coerceJsonArrayFromApi(section.productIds, []),
      categorySlugs: coerceJsonArrayFromApi(section.categorySlugs, []),
      categoryImages: coerceJsonObjectFromApi(section.categoryImages, {}),
      brands: coerceJsonArrayFromApi(section.brands, []),
    })),
  }
}

export function useCommerceMarketplaceAdmin() {
  const commerceLoading = ref(false)
  const commerceSaving = ref(false)
  const commerceSaveSuccess = ref(false)
  const commerceSaveError = ref('')
  /** 已成功載入／儲存後的 settings 快照，供 Tab／區塊 dirty 比對 */
  const settingsBaseline = ref(null)
  const storefrontFrontUrlBaseline = ref('')
  const commerceSettings = ref({
    commerceMode: 'b2c',
    settlementModes: ['online_gateway'],
    orderCaptureOnly: false,
    allowPointsCash: false,
    allowFullPointsOnly: true,
    storefrontTemplateId: 'standard',
    themeId: 'default',
    storefrontAppearance: readAppearance(null),
    menuItems: [],
    footerColumns: [],
    footerLegalText: '',
    footerConfig: { phone: '', email: '', serviceHours: '', socials: [], showPayments: true },
    homepageConfig: { sections: [], announcement: { text: '', href: '', enabled: true } },
    urlConventions: {},
    deliveryTimeConfig: { enabled: false, weekdaySlots: [], holidaySlots: [] },
    scheduledFulfillment: { enabled: false, defaultLeadDays: 3 },
    gtmContainerId: '',
    ga4MeasurementId: '',
    analyticsConsentRequired: true,
    invoiceProvider: '',
    invoiceEnabled: false,
    invoiceMerchantId: '',
    invoiceHashKey: '',
    invoiceHashIv: '',
    invoiceIsSandbox: true,
    invoiceAutoIssue: false,
    invoiceOptions: {
      allowCompany: true,
      personalCarriers: ['mobile', 'email'],
      companyCarriers: ['company_email', 'ship_with_goods'],
    },
    storefrontHostname: '',
    storefrontPublicBaseUrl: '',
    /** 多 LINE OA 時指定商城綁定之 Messaging Channel ID；單一 OA 可留空 */
    storefrontMessagingChannelId: '',
    /** true：須登入會員始可結帳；false：允許訪客／非會員下單（依前台實作） */
    checkoutRequiresLogin: true,
    /** 電商單專用扣庫倉庫（非簡易模式生效） */
    ecomWarehouseId: '',
    /** 前台顯示名稱（無 Logo 時顯示） */
    storefrontDisplayName: '',
    /** 前台 Logo URL（有值時優先顯示圖片） */
    storefrontLogoUrl: '',
    storefrontTemplateCategories: null,
    /** 銀行匯款資訊（對應 DB bank_transfer JSONB，鍵名與前台結帳顯示一致） */
    bankTransfer: {},
  })
  /** GET /settings 回傳：已設定 Channel ID 的 LINE 官方帳號選項 */
  const lineOaOptions = ref([])
  /** 商城設定「一般」分頁：單一輸入框，儲存時拆成 hostname + publicBaseUrl */
  const storefrontFrontUrl = ref('')
  /** GET settings 回傳：依 LINE Channel 推算之 https://{id}-mall.lockline.io（只讀參考） */
  const suggestedDefaultStorefrontUrl = ref('')
  const paymentProviders = ref([])
  const invoiceProviders = ref([])
  const logisticsProviders = ref([])
  const newProvider = ref({ provider_code: '', is_sandbox: true })
  const newProviderCustomCode = ref('')
  const newProviderDisplayName = ref('')
  const newLogisticsProvider = ref({ provider_code: '', is_sandbox: true })
  const newLogisticsProviderCustomCode = ref('')
  const newLogisticsProviderDisplayName = ref('')
  const newInvoiceProvider = ref({ provider_code: '', is_sandbox: true })
  const invoiceProviderOptionsToAdd = computed(() => {
    const added = new Set(
      invoiceProviders.value
        .filter((p) => !p._draftDeleted)
        .map((p) => String(p.provider_code || '').toLowerCase())
    )
    return INVOICE_PROVIDER_PRESET_SELECT_OPTIONS.filter((opt) => !added.has(opt.value))
  })

  const newProviderResolvedCode = computed(() => {
    const c = newProvider.value.provider_code
    if (!c) return ''
    if (c === '__custom__') return newProviderCustomCode.value.trim().toLowerCase()
    return String(c).trim().toLowerCase()
  })
  const newLogisticsProviderResolvedCode = computed(() => {
    const c = newLogisticsProvider.value.provider_code
    if (!c) return ''
    if (c === '__custom__') return newLogisticsProviderCustomCode.value.trim().toLowerCase()
    return String(c).trim().toLowerCase()
  })

  /** 後台外觀分頁：來自 API 的模板清單，缺漏時用常數預設 */
  const storefrontCategoriesResolved = computed(() => {
    const c = commerceSettings.value.storefrontTemplateCategories
    const src = Array.isArray(c) && c.length ? c : DEFAULT_STOREFRONT_TEMPLATE_CATEGORIES
    return src.map((cat) => ({
      ...cat,
      templates: Array.isArray(cat.templates)
        ? cat.templates.map((t) => {
            const value = String(t?.value ?? '')
              .trim()
              .toLowerCase()
            return {
              ...t,
              disabled: Boolean(t?.disabled) || !IMPLEMENTED_STOREFRONT_TEMPLATE_IDS.has(value),
            }
          })
        : [],
    }))
  })

  const storefrontTemplateFlatOptions = computed(() =>
    storefrontCategoriesResolved.value.flatMap((cat) =>
      cat.templates.map((t) => ({
        ...t,
        categoryId: cat.id,
        categoryLabel: cat.label,
      }))
    )
  )

  /** 目前選定之設計模板所允許的主題（配色）；來自全域登錄，每模板可有多組對應前台 CSS */
  const storefrontThemeOptionsForSelectedTemplate = computed(() => {
    const id = commerceSettings.value.storefrontTemplateId
    const templatePresetList = getStorefrontThemePresetsForTemplate(id)
    const templatePresetByValue = new Map(
      templatePresetList.map((x) => [String(x.value ?? '').trim().toLowerCase(), x])
    )
    const opt = storefrontTemplateFlatOptions.value.find((o) => o.value === id)
    if (opt && Array.isArray(opt.themes) && opt.themes.length) {
      return opt.themes.map((t) => {
        const key = String(t.value ?? '')
          .trim()
          .toLowerCase()
        const preset = templatePresetByValue.get(key)
        if (!preset) return t
        return {
          ...t,
          preview: preset.preview || t.preview,
          label: t.label || preset.label,
          description: t.description || preset.description,
        }
      })
    }
    return templatePresetList
  })

  watch(
    () => ({
      templateId: commerceSettings.value.storefrontTemplateId,
      themeKeys: storefrontThemeOptionsForSelectedTemplate.value.map((x) => x.value).join(','),
    }),
    () => {
      const opts = storefrontThemeOptionsForSelectedTemplate.value
      if (!opts.length) return
      const cur = commerceSettings.value.themeId
      if (!opts.some((o) => o.value === cur)) {
        commerceSettings.value.themeId = opts[0].value
      }
    }
  )

  const providerCredentials = ref({})
  const providerCredSaving = ref({})
  const providerCredSuccess = ref({})
  const providerCredError = ref({})
  /** 金流／發票／物流草稿對照（延遲寫入用） */
  const integrationBaseline = ref({ payment: null, invoice: null, logistics: null })
  const expandedProvider = ref(null)
  const newCredentialKey = ref('')
  const credentialSecretVisible = ref({})
  const providerKey = (channel, code) => `${String(channel)}::${String(code)}`
  const credentialSecretMapKey = (channel, code, fieldKey) =>
    `${providerKey(channel, code)}::${String(fieldKey)}`
  const isSecretVisible = (code, fieldKey, channel = 'payment') =>
    !!credentialSecretVisible.value[credentialSecretMapKey(channel, code, fieldKey)]
  const toggleSecretVisible = (code, fieldKey, channel = 'payment') => {
    const k = credentialSecretMapKey(channel, code, fieldKey)
    credentialSecretVisible.value = {
      ...credentialSecretVisible.value,
      [k]: !credentialSecretVisible.value[k],
    }
  }

  const getProviderFields = (code, channel = 'payment') =>
    PROVIDER_FIELDS_BY_CHANNEL[channel]?.[code?.toLowerCase()] ?? []

  const initProviderCredentials = (p, channel = 'payment') => {
    const existing = p.credentials ?? {}
    const fields = getProviderFields(p.provider_code, channel)
    const k = providerKey(channel, p.provider_code)
    if (fields.length) {
      const obj = {}
      fields.forEach((f) => {
        obj[f.key] = existing[f.key] ?? ''
      })
      providerCredentials.value[k] = obj
    } else {
      providerCredentials.value[k] = { ...existing }
    }
    if (channel === 'logistics' && String(p.provider_code || '').toLowerCase() === 'ecpay') {
      const savedMode = String(existing.logisticsMode || '').trim().toLowerCase()
      p.logistics_mode_draft = savedMode === 'c2c' ? 'c2c' : 'b2c'
      p.logistics_mode_saved = savedMode === 'b2c' || savedMode === 'c2c'
    }
  }

  const loadCommerceSettings = async () => {
    commerceLoading.value = true
    try {
      const [settingsRes, providersRes] = await Promise.all([
        commerceSettingsApi.getSettings(),
        commerceSettingsApi.listIntegrationProviders(),
      ])
      const s = settingsRes?.data ?? {}
      const loadedMode = s.commerceMode ?? 'b2c'
      let loadedSettlement = s.settlementModes ?? ['online_gateway']
      if (loadedMode === 'b2c' && Array.isArray(loadedSettlement)) {
        loadedSettlement = loadedSettlement.filter((x) => x !== 'monthly_statement')
        if (!loadedSettlement.length) loadedSettlement = ['online_gateway']
      }
      commerceSettings.value = {
        commerceMode: loadedMode,
        settlementModes: loadedSettlement,
        orderCaptureOnly: Boolean(s.orderCaptureOnly),
        allowPointsCash: s.allowPointsCash ?? false,
        allowFullPointsOnly: s.allowFullPointsOnly ?? true,
        storefrontTemplateId: s.storefrontTemplateId ?? 'standard',
        themeId: s.themeId ?? 'default',
        storefrontAppearance: readAppearance(s.storefrontAppearance, s.themeId),
        menuItems: coerceJsonArrayFromApi(s.menuItems, []).map(item => ({ ...item, position: item.position || 'main' })),
        footerColumns: coerceJsonArrayFromApi(s.footerColumns, []),
        footerLegalText: s.footerLegalText ?? '',
        footerConfig: { phone: '', email: '', serviceHours: '', socials: [], showPayments: true, ...coerceJsonObjectFromApi(s.footerConfig, {}) },
        homepageConfig: homepageSettingsFromApi(s.homepageConfig),
        urlConventions: coerceJsonObjectFromApi(s.urlConventions, {}),
        deliveryTimeConfig: coerceJsonObjectFromApi(s.deliveryTimeConfig, {
          enabled: false,
          weekdaySlots: [],
          holidaySlots: [],
        }),
        scheduledFulfillment: coerceJsonObjectFromApi(s.scheduledFulfillment, {
          enabled: false,
          defaultLeadDays: 3,
        }),
        gtmContainerId: s.gtmContainerId ?? '',
        ga4MeasurementId: s.ga4MeasurementId ?? '',
        analyticsConsentRequired: s.analyticsConsentRequired ?? true,
        invoiceProvider: s.invoiceProvider ?? '',
        invoiceEnabled: Boolean(s.invoiceEnabled),
        invoiceMerchantId: s.invoiceMerchantId ?? '',
        invoiceHashKey: s.invoiceHashKey ?? '',
        invoiceHashIv: s.invoiceHashIv ?? '',
        invoiceIsSandbox: s.invoiceIsSandbox !== undefined ? Boolean(s.invoiceIsSandbox) : true,
        invoiceAutoIssue: Boolean(s.invoiceAutoIssue),
        invoiceOptions: invoiceOptionsFromSettings(s.invoiceOptions),
        storefrontHostname: s.storefrontHostname != null ? String(s.storefrontHostname) : '',
        storefrontPublicBaseUrl:
          s.storefrontPublicBaseUrl != null ? String(s.storefrontPublicBaseUrl) : '',
        storefrontMessagingChannelId:
          s.storefrontMessagingChannelId != null && String(s.storefrontMessagingChannelId).trim() !== ''
            ? String(s.storefrontMessagingChannelId).trim()
            : '',
        checkoutRequiresLogin:
          s.checkoutRequiresLogin === undefined ? true : Boolean(s.checkoutRequiresLogin),
        ecomWarehouseId:
          s.ecomWarehouseId != null && String(s.ecomWarehouseId).trim() !== ''
            ? String(s.ecomWarehouseId).trim()
            : '',
        storefrontDisplayName:
          s.storefrontDisplayName != null ? String(s.storefrontDisplayName).trim() : '',
        storefrontLogoUrl:
          s.storefrontLogoUrl != null && String(s.storefrontLogoUrl).trim() !== ''
            ? String(s.storefrontLogoUrl).trim()
            : '',
        storefrontTemplateCategories: Array.isArray(s.storefrontTemplateCategories)
          ? s.storefrontTemplateCategories
          : null,
        bankTransfer: coerceJsonObjectFromApi(s.bankTransfer, {}),
      }
      lineOaOptions.value = Array.isArray(s.lineOaOptions) ? s.lineOaOptions : []
      const items = (providersRes?.data?.items ?? []).map((row) => ({
        ...row,
        display_name_draft: row.display_name ?? '',
      }))
      paymentProviders.value = items.filter((x) => String(x.channel || '') === 'payment')
      invoiceProviders.value = items.filter((x) => String(x.channel || '') === 'invoice')
      logisticsProviders.value = items.filter((x) => String(x.channel || '') === 'logistics')
      if (!invoiceProviders.value.length && String(commerceSettings.value.invoiceProvider || '').trim()) {
        invoiceProviders.value = [{
          channel: 'invoice',
          provider_code: String(commerceSettings.value.invoiceProvider).trim().toLowerCase(),
          is_enabled: Boolean(commerceSettings.value.invoiceEnabled),
          is_sandbox: commerceSettings.value.invoiceIsSandbox !== false,
          display_order: 0,
          display_name: null,
          display_name_draft: '',
          credentials: {
            MerchantID: commerceSettings.value.invoiceMerchantId || '',
            HashKey: commerceSettings.value.invoiceHashKey || '',
            HashIV: commerceSettings.value.invoiceHashIv || '',
          },
        }]
      }
      paymentProviders.value.forEach((p) => initProviderCredentials(p, 'payment'))
      invoiceProviders.value.forEach((p) => initProviderCredentials(p, 'invoice'))
      logisticsProviders.value.forEach((p) => initProviderCredentials(p, 'logistics'))
      storefrontFrontUrl.value = composeStorefrontFrontUrl(
        commerceSettings.value.storefrontHostname,
        commerceSettings.value.storefrontPublicBaseUrl
      )
      const sug = s.suggestedDefaultStorefrontUrl
      suggestedDefaultStorefrontUrl.value =
        sug != null && String(sug).trim() ? String(sug).trim().replace(/\/+$/, '') : ''
      captureSettingsBaseline()
      captureIntegrationBaseline()
    } catch (e) {
      console.error('[CommerceSettings] loadCommerceSettings error:', e)
    } finally {
      commerceLoading.value = false
    }
  }

  function cloneJson(value) {
    return JSON.parse(JSON.stringify(value ?? null))
  }

  function providersRefForChannel(channel) {
    if (channel === 'invoice') return invoiceProviders
    if (channel === 'logistics') return logisticsProviders
    return paymentProviders
  }

  function snapshotIntegrationChannel(channel) {
    const list = providersRefForChannel(channel).value
    const providers = list
      .filter((p) => !p._draftDeleted)
      .map((p) => ({
        provider_code: p.provider_code,
        is_enabled: Boolean(p.is_enabled),
        is_sandbox: p.is_sandbox !== false,
        display_order: p.display_order ?? 0,
        display_name: p.display_name ?? null,
        display_name_draft: p.display_name_draft ?? '',
        logistics_mode_draft: p.logistics_mode_draft ?? 'b2c',
        logistics_mode_saved: Boolean(p.logistics_mode_saved),
        _draftNew: Boolean(p._draftNew),
      }))
    const credentials = {}
    for (const p of list) {
      if (p._draftDeleted) continue
      const k = providerKey(channel, p.provider_code)
      credentials[k] = cloneJson(providerCredentials.value[k] || {})
    }
    const snap = { providers, credentials }
    if (channel === 'invoice') {
      snap.invoiceOptions = cloneJson(commerceSettings.value.invoiceOptions)
    }
    return snap
  }

  function captureIntegrationBaseline(channel) {
    if (channel) {
      integrationBaseline.value = {
        ...integrationBaseline.value,
        [channel]: snapshotIntegrationChannel(channel),
      }
      return
    }
    integrationBaseline.value = {
      payment: snapshotIntegrationChannel('payment'),
      invoice: snapshotIntegrationChannel('invoice'),
      logistics: snapshotIntegrationChannel('logistics'),
    }
  }

  function isIntegrationChannelDirty(channel) {
    const base = integrationBaseline.value[channel]
    if (!base) return false
    const list = providersRefForChannel(channel).value
    if (list.some((p) => p._draftNew || p._draftDeleted)) return true
    const active = list.filter((p) => !p._draftDeleted && !p._draftNew)
    if (active.length !== base.providers.filter((p) => !p._draftNew).length) return true
    for (const p of active) {
      const bp = base.providers.find(
        (x) => String(x.provider_code).toLowerCase() === String(p.provider_code).toLowerCase()
      )
      if (!bp) return true
      if (Boolean(p.is_enabled) !== Boolean(bp.is_enabled)) return true
      if ((p.is_sandbox !== false) !== (bp.is_sandbox !== false)) return true
      if (String(p.display_name_draft ?? '') !== String(bp.display_name_draft ?? '')) return true
      if (channel === 'logistics') {
        if (String(p.logistics_mode_draft || 'b2c') !== String(bp.logistics_mode_draft || 'b2c')) return true
      }
      const k = providerKey(channel, p.provider_code)
      if (JSON.stringify(providerCredentials.value[k] || {}) !== JSON.stringify(base.credentials[k] || {})) {
        return true
      }
    }
    if (channel === 'invoice') {
      if (JSON.stringify(commerceSettings.value.invoiceOptions ?? null) !== JSON.stringify(base.invoiceOptions ?? null)) {
        return true
      }
    }
    return false
  }

  function revertIntegrationChannel(channel) {
    const base = integrationBaseline.value[channel]
    if (!base) return
    const listRef = providersRefForChannel(channel)
    const restored = cloneJson(base.providers).map((p) => {
      const { _draftNew, ...rest } = p
      return { ...rest, _draftNew: false, _draftDeleted: false }
    })
    listRef.value = restored
    const prefix = `${channel}::`
    for (const key of Object.keys(providerCredentials.value)) {
      if (key.startsWith(prefix)) delete providerCredentials.value[key]
    }
    for (const [k, v] of Object.entries(base.credentials || {})) {
      providerCredentials.value[k] = cloneJson(v)
    }
    for (const p of listRef.value) {
      if (channel === 'logistics' && String(p.provider_code || '').toLowerCase() === 'ecpay') {
        const existing = providerCredentials.value[providerKey(channel, p.provider_code)] || {}
        const savedMode = String(existing.logisticsMode || '').trim().toLowerCase()
        p.logistics_mode_draft = savedMode === 'c2c' ? 'c2c' : (p.logistics_mode_draft || 'b2c')
        p.logistics_mode_saved = savedMode === 'b2c' || savedMode === 'c2c'
      }
    }
    if (channel === 'invoice' && base.invoiceOptions) {
      commerceSettings.value.invoiceOptions = cloneJson(base.invoiceOptions)
    }
    expandedProvider.value = null
  }

  async function reloadIntegrationChannelFromApi(channel) {
    const providersRes = await commerceSettingsApi.listIntegrationProviders()
    const items = (providersRes?.data?.items ?? []).map((row) => ({
      ...row,
      display_name_draft: row.display_name ?? '',
    }))
    const filtered = items.filter((x) => String(x.channel || '') === channel)
    const listRef = providersRefForChannel(channel)
    listRef.value = filtered
    const prefix = `${channel}::`
    for (const key of Object.keys(providerCredentials.value)) {
      if (key.startsWith(prefix)) delete providerCredentials.value[key]
    }
    filtered.forEach((p) => initProviderCredentials(p, channel))
    if (channel === 'invoice') {
      const settingsRes = await commerceSettingsApi.getSettings()
      const s = settingsRes?.data ?? {}
      commerceSettings.value.invoiceOptions = invoiceOptionsFromSettings(s.invoiceOptions)
      captureSettingsBaseline(['invoiceOptions'])
    }
    captureIntegrationBaseline(channel)
  }

  function captureSettingsBaseline(keys) {
    const src = commerceSettings.value
    if (!keys || !keys.length) {
      settingsBaseline.value = cloneJson(src)
      storefrontFrontUrlBaseline.value = String(storefrontFrontUrl.value ?? '')
      return
    }
    const next = settingsBaseline.value ? { ...settingsBaseline.value } : {}
    for (const key of keys) {
      next[key] = cloneJson(src[key])
    }
    settingsBaseline.value = next
    if (keys.some((k) => ['storefrontHostname', 'storefrontPublicBaseUrl'].includes(k))) {
      storefrontFrontUrlBaseline.value = String(storefrontFrontUrl.value ?? '')
    }
  }

  function isScopeDirty(scope) {
    if (scope === 'integration_payment') return isIntegrationChannelDirty('payment')
    if (scope === 'integration_invoice') return isIntegrationChannelDirty('invoice')
    if (scope === 'integration_logistics') return isIntegrationChannelDirty('logistics')
    const keys = COMMERCE_SETTINGS_SCOPE_KEYS[scope]
    if (!keys) return false
    if (!keys.length) return false
    if (!settingsBaseline.value) return false
    for (const key of keys) {
      if (JSON.stringify(commerceSettings.value[key] ?? null) !== JSON.stringify(settingsBaseline.value[key] ?? null)) {
        return true
      }
    }
    if (scope === 'general') {
      if (String(storefrontFrontUrl.value ?? '') !== String(storefrontFrontUrlBaseline.value ?? '')) {
        return true
      }
    }
    return false
  }

  function isTabDirty(tab) {
    if (tab === 'integration') {
      return (
        isScopeDirty('integration_payment') ||
        isScopeDirty('integration_invoice') ||
        isScopeDirty('integration_logistics')
      )
    }
    if (tab === 'appearance' || tab === 'monthly_credit') return false
    return isScopeDirty(tab)
  }

  function revertScope(scope) {
    if (scope === 'integration_payment') {
      revertIntegrationChannel('payment')
      return
    }
    if (scope === 'integration_invoice') {
      revertIntegrationChannel('invoice')
      return
    }
    if (scope === 'integration_logistics') {
      revertIntegrationChannel('logistics')
      return
    }
    const keys = COMMERCE_SETTINGS_SCOPE_KEYS[scope]
    if (!keys?.length || !settingsBaseline.value) return
    for (const key of keys) {
      commerceSettings.value[key] = cloneJson(settingsBaseline.value[key])
    }
    if (scope === 'general') {
      storefrontFrontUrl.value = String(storefrontFrontUrlBaseline.value ?? '')
    }
  }

  function revertTab(tab) {
    if (tab === 'integration') {
      revertIntegrationChannel('payment')
      revertIntegrationChannel('invoice')
      revertIntegrationChannel('logistics')
      return
    }
    if (tab === 'appearance' || tab === 'monthly_credit') return
    revertScope(tab)
  }

  function composeStorefrontFrontUrl(hostname, publicUrl) {
    const pub = String(publicUrl ?? '')
      .trim()
      .replace(/\/+$/, '')
    const host = String(hostname ?? '').trim()
    if (pub) return pub
    if (host) return `https://${host}`
    return ''
  }

  /** 將使用者輸入的前台網址拆成 hostname（比對租戶）與對外 origin（含協定與 port） */
  function parseStorefrontFrontUrlToFields(raw) {
    const t = String(raw ?? '').trim()
    if (!t) return { hostname: '', publicBaseUrl: '' }
    let urlStr = t
    if (!/^https?:\/\//i.test(urlStr)) {
      urlStr = `https://${urlStr.replace(/^\/+/, '').split('/')[0] || ''}`
    }
    if (!urlStr || urlStr === 'https://') return { hostname: '', publicBaseUrl: '' }
    try {
      const u = new URL(urlStr)
      const hostname = (u.hostname || '').toLowerCase()
      if (!hostname) return { hostname: '', publicBaseUrl: '' }
      const defaultHttps = u.protocol === 'https:' && (!u.port || u.port === '443')
      const defaultHttp = u.protocol === 'http:' && (!u.port || u.port === '80')
      const origin =
        defaultHttps || defaultHttp
          ? `${u.protocol}//${u.hostname}`
          : `${u.protocol}//${u.hostname}${u.port ? `:${u.port}` : ''}`
      return { hostname, publicBaseUrl: origin.replace(/\/$/, '') }
    } catch {
      return null
    }
  }

  function applySuggestedStorefrontUrl() {
    const u = suggestedDefaultStorefrontUrl.value.trim()
    if (!u) {
      commerceSaveError.value =
        '目前無法取得系統預設網址。請先於「LINE 官方帳號設定」建立 Messaging API，並填寫 Channel ID。'
      window.setTimeout(() => {
        if (
          commerceSaveError.value ===
          '目前無法取得系統預設網址。請先於「LINE 官方帳號設定」建立 Messaging API，並填寫 Channel ID。'
        ) {
          commerceSaveError.value = ''
        }
      }, 6000)
      return
    }
    storefrontFrontUrl.value = u
    commerceSaveError.value = ''
  }

  const toggleOrderCaptureOnly = () => {
    const next = !commerceSettings.value.orderCaptureOnly
    commerceSettings.value.orderCaptureOnly = next
    if (next) expandedProvider.value = null
  }

  /**
   * 局部儲存商城設定。
   * @param {object} [options]
   * @param {string} [options.scope] COMMERCE_SETTINGS_SCOPE_KEYS 的 key；未傳則相容舊行為（送一般相關可存欄位全集，不含外觀）
   * @param {string[]} [options.keys] 直接指定欄位；優先於 scope
   */
  const handleCommerceSave = async (options = {}) => {
    const scope = options.scope || null
    const keys =
      Array.isArray(options.keys) && options.keys.length
        ? options.keys
        : scope
          ? COMMERCE_SETTINGS_SCOPE_KEYS[scope] || []
          : null

    if (Array.isArray(keys) && keys.length === 0) {
      commerceSaveError.value = ''
      commerceSaveSuccess.value = true
      setTimeout(() => {
        commerceSaveSuccess.value = false
      }, 3000)
      return true
    }

    commerceSaving.value = true
    commerceSaveSuccess.value = false
    commerceSaveError.value = ''
    try {
      const needsFrontUrl =
        !keys ||
        keys.includes('storefrontHostname') ||
        keys.includes('storefrontPublicBaseUrl')
      if (needsFrontUrl) {
        const parsed = parseStorefrontFrontUrlToFields(storefrontFrontUrl.value)
        if (parsed == null) {
          commerceSaveError.value = '前台網址格式不正確，請輸入完整網址（含 http:// 或 https://）或僅網域'
          return false
        }
        commerceSettings.value.storefrontHostname = parsed.hostname
        commerceSettings.value.storefrontPublicBaseUrl = parsed.publicBaseUrl
        storefrontFrontUrl.value = composeStorefrontFrontUrl(parsed.hostname, parsed.publicBaseUrl)
      }

      const needsBankTransfer = !keys || keys.includes('bankTransfer') || keys.includes('settlementModes')
      if (needsBankTransfer) {
        const bankTransferErr = getBankTransferSaveValidationMessage(
          commerceSettings.value.settlementModes,
          commerceSettings.value.bankTransfer
        )
        if (bankTransferErr) {
          commerceSaveError.value = bankTransferErr
          return false
        }
      }

      const full = JSON.parse(JSON.stringify(commerceSettings.value))
      delete full.storefrontAppearance
      delete full.themeId
      for (const field of LEGACY_INVOICE_FIELDS_NEVER_IN_PATCH) delete full[field]

      let body
      if (keys) {
        body = {}
        for (const key of keys) {
          if (LEGACY_INVOICE_FIELDS_NEVER_IN_PATCH.includes(key)) continue
          if (key === 'storefrontAppearance' || key === 'themeId') continue
          if (full[key] !== undefined) body[key] = full[key]
          else body[key] = commerceSettings.value[key]
        }
      } else {
        body = full
      }

      if (body.bankTransfer && typeof body.bankTransfer === 'object' && !Array.isArray(body.bankTransfer)) {
        const cleaned = {}
        for (const [k, v] of Object.entries(body.bankTransfer)) {
          if (v == null) continue
          const s = String(v).trim()
          if (s) cleaned[k] = s
        }
        body.bankTransfer = cleaned
        commerceSettings.value.bankTransfer = cleaned
      }

      if (!Object.keys(body).length) {
        commerceSaveSuccess.value = true
        setTimeout(() => {
          commerceSaveSuccess.value = false
        }, 3000)
        return true
      }

      const saved = await commerceSettingsApi.updateSettings(body)
      if (saved.data?.storefrontAppearance) {
        commerceSettings.value.storefrontAppearance = readAppearance(saved.data.storefrontAppearance)
      }
      captureSettingsBaseline(keys || Object.keys(body))

      commerceSaveSuccess.value = true
      setTimeout(() => {
        commerceSaveSuccess.value = false
      }, 3000)
      return true
    } catch (e) {
      commerceSaveError.value = e?.response?.data?.message ?? '儲存失敗，請稍後再試'
      return false
    } finally {
      commerceSaving.value = false
    }
  }

  const onProviderEnabledSwitch = (p) => {
    p.is_enabled = !p.is_enabled
  }

  const handleAddProvider = () => {
    const code = newProviderResolvedCode.value
    if (!code) return
    if (
      paymentProviders.value.some(
        (p) => !p._draftDeleted && String(p.provider_code).toLowerCase() === code
      )
    ) {
      commerceSaveError.value = '此金流服務已存在於清單中'
      return
    }
    commerceSaveError.value = ''
    const displayName =
      newProvider.value.provider_code === '__custom__' ? String(newProviderDisplayName.value || '').trim() : ''
    const draft = {
      channel: 'payment',
      provider_code: code,
      is_sandbox: newProvider.value.is_sandbox !== false,
      is_enabled: false,
      display_order: paymentProviders.value.filter((p) => !p._draftDeleted).length,
      display_name: displayName || null,
      display_name_draft: displayName,
      credentials: {},
      _draftNew: true,
      _draftDeleted: false,
    }
    paymentProviders.value.push(draft)
    initProviderCredentials(draft, 'payment')
    newProvider.value = { provider_code: '', is_sandbox: true }
    newProviderCustomCode.value = ''
    newProviderDisplayName.value = ''
  }

  const paymentProviderDeleteTarget = ref(null)
  const paymentProviderDeleting = ref(false)

  const handleDeleteProvider = (p) => {
    if (!p?.provider_code) return
    paymentProviderDeleteTarget.value = p
  }

  const cancelDeletePaymentProvider = () => {
    if (paymentProviderDeleting.value) return
    paymentProviderDeleteTarget.value = null
  }

  const confirmDeletePaymentProvider = () => {
    const p = paymentProviderDeleteTarget.value
    const code = p?.provider_code
    if (!code) return
    if (p._draftNew) {
      paymentProviders.value = paymentProviders.value.filter((x) => x !== p)
      delete providerCredentials.value[providerKey('payment', code)]
    } else {
      p._draftDeleted = true
    }
    if (expandedProvider.value === providerKey('payment', code)) expandedProvider.value = null
    paymentProviderDeleteTarget.value = null
  }

  const fillEcpaySandboxCredentials = (code) => {
    const k = providerKey('payment', code)
    const prev = providerCredentials.value[k] || {}
    providerCredentials.value[k] = { ...prev, ...ECPAY_OFFICIAL_SANDBOX_CREDENTIALS }
  }

  const fillEcpayLogisticsSandboxCredentials = (provider) => {
    const code = provider.provider_code
    const k = providerKey('logistics', code)
    const prev = providerCredentials.value[k] || {}
    const mode = provider.logistics_mode_draft === 'c2c' ? 'c2c' : 'b2c'
    const merchant = mode === 'c2c'
      ? ECPAY_LOGISTICS_C2C_SANDBOX_CREDENTIALS
      : ECPAY_LOGISTICS_B2C_SANDBOX_CREDENTIALS
    providerCredentials.value[k] = { ...prev, ...merchant, ...ECPAY_LOGISTICS_SANDBOX_SENDER }
  }

  const fillEcpayInvoiceSandboxCredentials = (code) => {
    const k = providerKey('invoice', code)
    const prev = providerCredentials.value[k] || {}
    providerCredentials.value[k] = {
      ...prev,
      MerchantID: ECPAY_OFFICIAL_SANDBOX_INVOICE.invoiceMerchantId,
      HashKey: ECPAY_OFFICIAL_SANDBOX_INVOICE.invoiceHashKey,
      HashIV: ECPAY_OFFICIAL_SANDBOX_INVOICE.invoiceHashIv,
    }
  }

  const toggleProviderExpand = (code, channel = 'payment') => {
    const k = providerKey(channel, code)
    expandedProvider.value = expandedProvider.value === k ? null : k
  }

  const setCredentialField = (code, key, val, channel = 'payment') => {
    const k = providerKey(channel, code)
    if (!providerCredentials.value[k]) providerCredentials.value[k] = {}
    providerCredentials.value[k][key] = val
  }

  const addCredentialField = (code, channel = 'payment') => {
    if (!newCredentialKey.value) return
    setCredentialField(code, newCredentialKey.value, '', channel)
    newCredentialKey.value = ''
  }

  const removeCredentialField = (code, key, channel = 'payment') => {
    const k = providerKey(channel, code)
    if (providerCredentials.value[k]) {
      delete providerCredentials.value[k][key]
      providerCredentials.value[k] = { ...providerCredentials.value[k] }
    }
  }

  const handleSaveCredentials = async (provider, channel = 'payment', options = {}) => {
    const code = provider.provider_code
    const k = providerKey(channel, code)
    providerCredSaving.value[k] = true
    providerCredSuccess.value[k] = false
    providerCredError.value[k] = ''
    try {
      const cleanedCredentials = sanitizeIntegrationCredentials(
        channel,
        code,
        providerCredentials.value[k] ?? {}
      )
      providerCredentials.value[k] = cleanedCredentials
      const payload = {
        channel,
        provider_code: code,
        is_enabled: provider.is_enabled,
        is_sandbox: provider.is_sandbox,
        display_order: provider.display_order ?? 0,
        credentials: { ...cleanedCredentials },
      }
      if (channel === 'logistics' && String(code).toLowerCase() === 'ecpay') {
        delete payload.credentials.LogisticsSubTypePolicies
        if (options.persistLogisticsProfile) {
          const mode = provider.logistics_mode_draft === 'c2c' ? 'c2c' : 'b2c'
          payload.credentials.logisticsMode = mode
          payload.persist_logistics_profile = true
          const missingLabels = ['SenderName', 'SenderZipCode', 'SenderAddress']
            .filter((key) => !String(payload.credentials[key] ?? '').trim())
            .map((key) => ({ SenderName: '寄件人姓名', SenderZipCode: '寄件人郵遞區號', SenderAddress: '寄件人地址' }[key]))
          if (missingLabels.length) {
            providerCredError.value[k] = `請填寫${missingLabels.join('、')}`
            return
          }
        }
      }
      if (!isCatalogIntegrationProvider(channel, code)) {
        payload.display_name = provider.display_name_draft ?? ''
      }
      if (channel === 'invoice') {
        const missingLabels = getProviderFields(code, 'invoice')
          .filter((field) => field.required && !String(payload.credentials?.[field.key] ?? '').trim())
          .map((field) => field.label)
        if (missingLabels.length) {
          providerCredError.value[k] = `請填寫${missingLabels.join('、')}`
          return
        }
        payload.credentials = {
          ...payload.credentials,
          autoIssue: Boolean(provider.is_enabled),
        }
      }
      const res = await commerceSettingsApi.upsertIntegrationProvider(payload)
      if (channel === 'logistics' && String(code).toLowerCase() === 'ecpay' && options.persistLogisticsProfile) {
        provider.logistics_mode_saved = true
      }
      if (!isCatalogIntegrationProvider(channel, code)) {
        const savedName = res?.data?.display_name ?? null
        provider.display_name = savedName
        provider.display_name_draft = savedName ?? ''
      }
      providerCredSuccess.value[k] = true
      setTimeout(() => {
        providerCredSuccess.value[k] = false
      }, 3000)
    } catch (e) {
      providerCredError.value[k] = e?.response?.data?.message ?? '儲存失敗'
    } finally {
      providerCredSaving.value[k] = false
    }
  }

  const onLogisticsProviderEnabledSwitch = (p) => {
    p.is_enabled = !p.is_enabled
    if (p.is_enabled) {
      logisticsProviders.value.forEach((other) => {
        if (other.provider_code !== p.provider_code) other.is_enabled = false
      })
    }
  }

  const handleAddLogisticsProvider = () => {
    const code = newLogisticsProviderResolvedCode.value
    if (!code) return
    if (
      logisticsProviders.value.some(
        (p) => !p._draftDeleted && String(p.provider_code).toLowerCase() === code
      )
    ) {
      commerceSaveError.value = '此物流服務已存在於清單中'
      return
    }
    commerceSaveError.value = ''
    const displayName =
      newLogisticsProvider.value.provider_code === '__custom__'
        ? String(newLogisticsProviderDisplayName.value || '').trim()
        : ''
    const draft = {
      channel: 'logistics',
      provider_code: code,
      is_sandbox: newLogisticsProvider.value.is_sandbox !== false,
      is_enabled: false,
      display_order: logisticsProviders.value.filter((p) => !p._draftDeleted).length,
      display_name: displayName || null,
      display_name_draft: displayName,
      logistics_mode_draft: 'b2c',
      logistics_mode_saved: false,
      credentials: {},
      _draftNew: true,
      _draftDeleted: false,
    }
    logisticsProviders.value.push(draft)
    initProviderCredentials(draft, 'logistics')
    newLogisticsProvider.value = { provider_code: '', is_sandbox: true }
    newLogisticsProviderCustomCode.value = ''
    newLogisticsProviderDisplayName.value = ''
  }

  const handleDeleteLogisticsProvider = async (provider) => {
    const code = String(provider?.provider_code || '').trim()
    if (!code) return
    const ok = await showConfirm(
      '移除物流服務',
      `將標記刪除「${code}」，需按區塊「儲存」後才會真正刪除。確定嗎？`,
      '標記刪除',
      '取消'
    )
    if (!ok) return
    if (provider._draftNew) {
      logisticsProviders.value = logisticsProviders.value.filter((x) => x !== provider)
      delete providerCredentials.value[providerKey('logistics', code)]
    } else {
      provider._draftDeleted = true
    }
    if (expandedProvider.value === providerKey('logistics', code)) expandedProvider.value = null
  }

  const onInvoiceProviderEnabledSwitch = (provider) => {
    const next = !provider.is_enabled
    provider.is_enabled = next
    if (next) {
      invoiceProviders.value.forEach((other) => {
        if (other.provider_code !== provider.provider_code) other.is_enabled = false
      })
    }
  }

  const handleAddInvoiceProvider = () => {
    const code = String(newInvoiceProvider.value.provider_code || '').trim().toLowerCase()
    if (!code) return
    if (
      invoiceProviders.value.some(
        (p) => !p._draftDeleted && String(p.provider_code).toLowerCase() === code
      )
    ) {
      commerceSaveError.value = '此電子發票服務已存在於清單中'
      return
    }
    commerceSaveError.value = ''
    const draft = {
      channel: 'invoice',
      provider_code: code,
      is_sandbox: newInvoiceProvider.value.is_sandbox !== false,
      is_enabled: false,
      display_order: invoiceProviders.value.filter((p) => !p._draftDeleted).length,
      display_name: null,
      display_name_draft: '',
      credentials: {},
      _draftNew: true,
      _draftDeleted: false,
    }
    invoiceProviders.value.push(draft)
    initProviderCredentials(draft, 'invoice')
    newInvoiceProvider.value = { provider_code: '', is_sandbox: true }
  }

  const handleDeleteInvoiceProvider = async (provider) => {
    const code = String(provider?.provider_code || '').trim()
    if (!code) return
    const ok = await showConfirm(
      '移除電子發票服務',
      `將標記刪除「${code}」，需按區塊「儲存」後才會真正刪除。確定嗎？`,
      '標記刪除',
      '取消'
    )
    if (!ok) return
    if (provider._draftNew) {
      invoiceProviders.value = invoiceProviders.value.filter((x) => x !== provider)
      delete providerCredentials.value[providerKey('invoice', code)]
    } else {
      provider._draftDeleted = true
    }
    if (expandedProvider.value === providerKey('invoice', code)) expandedProvider.value = null
  }

  /**
   * 將單一 Provider 草稿寫入 API（供區塊儲存使用）。
   * @returns {Promise<string|null>} 失敗時回傳錯誤訊息，成功回傳 null
   */
  async function upsertProviderDraft(provider, channel) {
    const code = provider.provider_code
    const k = providerKey(channel, code)
    const cleanedCredentials = sanitizeIntegrationCredentials(
      channel,
      code,
      providerCredentials.value[k] ?? {}
    )
    providerCredentials.value[k] = cleanedCredentials
    const payload = {
      channel,
      provider_code: code,
      is_enabled: provider.is_enabled,
      is_sandbox: provider.is_sandbox,
      display_order: provider.display_order ?? 0,
      credentials: { ...cleanedCredentials },
    }
    if (channel === 'logistics' && String(code).toLowerCase() === 'ecpay') {
      delete payload.credentials.LogisticsSubTypePolicies
      const mode = provider.logistics_mode_draft === 'c2c' ? 'c2c' : 'b2c'
      payload.credentials.logisticsMode = mode
      payload.persist_logistics_profile = true
      const missingLabels = ['SenderName', 'SenderZipCode', 'SenderAddress']
        .filter((key) => !String(payload.credentials[key] ?? '').trim())
        .map((key) => ({ SenderName: '寄件人姓名', SenderZipCode: '寄件人郵遞區號', SenderAddress: '寄件人地址' }[key]))
      if (missingLabels.length) {
        return `請填寫${missingLabels.join('、')}`
      }
    }
    if (!isCatalogIntegrationProvider(channel, code)) {
      payload.display_name = provider.display_name_draft ?? ''
    }
    if (channel === 'invoice') {
      const missingLabels = getProviderFields(code, 'invoice')
        .filter((field) => field.required && !String(payload.credentials?.[field.key] ?? '').trim())
        .map((field) => field.label)
      if (missingLabels.length) {
        return `請填寫${missingLabels.join('、')}`
      }
      payload.credentials = {
        ...payload.credentials,
        autoIssue: Boolean(provider.is_enabled),
      }
    }
    await commerceSettingsApi.upsertIntegrationProvider(payload)
    return null
  }

  /**
   * 區塊「儲存」：確認後寫入該 channel 全部草稿（盡力執行＋ D／D2 摘要）。
   * @param {'payment'|'invoice'|'logistics'} channel
   */
  async function saveIntegrationSection(channel) {
    const label = INTEGRATION_SECTION_LABELS[channel] || channel
    if (!isIntegrationChannelDirty(channel)) {
      await showInfo('沒有變更', `「${label}」沒有需要儲存的內容`)
      return true
    }
    const confirmed = await showConfirm(
      `儲存${label}設定`,
      `確定要儲存「${label}」的變更嗎？包含啟用狀態、憑證、新增與刪除。`,
      '儲存',
      '取消',
      'question'
    )
    if (!confirmed) return false

    commerceSaving.value = true
    commerceSaveError.value = ''
    const successes = []
    const failures = []
    const listRef = providersRefForChannel(channel)

    try {
      for (const p of listRef.value.filter((x) => x._draftDeleted && !x._draftNew)) {
        const code = p.provider_code
        const chipLabel = integrationProviderChipLabel(channel, code, p.display_name_draft ?? p.display_name)
        try {
          await commerceSettingsApi.deleteIntegrationProvider(channel, code)
          successes.push({ label: chipLabel })
        } catch (e) {
          failures.push({
            label: chipLabel,
            error: e?.response?.data?.message ?? e?.message ?? '失敗',
          })
        }
      }

      for (const p of listRef.value.filter((x) => !x._draftDeleted)) {
        const code = p.provider_code
        const chipLabel = integrationProviderChipLabel(channel, code, p.display_name_draft ?? p.display_name)
        try {
          const err = await upsertProviderDraft(p, channel)
          if (err) {
            failures.push({ label: chipLabel, error: err })
            continue
          }
          successes.push({ label: chipLabel })
        } catch (e) {
          failures.push({
            label: chipLabel,
            error: e?.response?.data?.message ?? e?.message ?? '失敗',
          })
        }
      }

      if (channel === 'invoice') {
        try {
          await commerceSettingsApi.updateSettings({
            invoiceOptions: cloneJson(commerceSettings.value.invoiceOptions),
          })
          successes.push({ label: '發票載具' })
        } catch (e) {
          failures.push({
            label: '發票載具',
            error: e?.response?.data?.message ?? e?.message ?? '失敗',
          })
        }
      }

      try {
        await reloadIntegrationChannelFromApi(channel)
      } catch (e) {
        failures.push({
          label: '重新載入',
          error: e?.response?.data?.message ?? e?.message ?? '失敗',
        })
      }

      const html = buildIntegrationSaveResultHtml({ channel, successes, failures })
      if (!failures.length) {
        await Swal.fire({
          icon: 'success',
          title: '儲存成功',
          html,
          showConfirmButton: true,
          confirmButtonText: '確定',
          confirmButtonColor: '#667eea',
        })
        return true
      }
      if (!successes.length) {
        await Swal.fire({
          icon: 'error',
          title: '儲存失敗',
          html,
          confirmButtonText: '確定',
          confirmButtonColor: '#dc3545',
        })
        return false
      }
      await Swal.fire({
        icon: 'warning',
        title: '部分儲存完成',
        html,
        confirmButtonText: '確定',
        confirmButtonColor: '#ffc107',
      })
      return false
    } finally {
      commerceSaving.value = false
    }
  }

  function invoiceOptionsFromSettings(raw) {
    const source = raw && typeof raw === 'object' ? raw : {}
    const pick = (list, allowed, fallback) => Array.isArray(list) ? allowed.filter((item) => list.includes(item)) : fallback
    const aliases = { cloud: 'company_email', post: 'ship_with_goods' }
    const allowed = ['company_email', 'ship_with_goods']
    const rawCompany = Array.isArray(source.companyCarriers)
      ? source.companyCarriers.map((item) => aliases[item] || item)
      : null
    const companyCarriers = pick(rawCompany, allowed, allowed)
    return {
      allowCompany: companyCarriers.length > 0,
      personalCarriers: pick(source.personalCarriers, ['mobile', 'email'], ['mobile', 'email']),
      companyCarriers,
    }
  }

  function invoiceCarrierOn(group, value) {
    return (commerceSettings.value.invoiceOptions?.[group] || []).includes(value)
  }

  function toggleInvoiceCarrier(group, value) {
    const options = commerceSettings.value.invoiceOptions || { allowCompany: true, personalCarriers: [], companyCarriers: [] }
    const next = new Set(options[group] || [])
    if (next.has(value)) next.delete(value)
    else next.add(value)
    options[group] = [...next]
    options.allowCompany = (options.companyCarriers || []).length > 0
    commerceSettings.value.invoiceOptions = options
  }

  return {
    SETTLEMENT_MODE_OPTIONS,
    PROVIDER_PRESET_SELECT_OPTIONS,
    LOGISTICS_PROVIDER_PRESET_SELECT_OPTIONS,
    INVOICE_PROVIDER_PRESET_SELECT_OPTIONS,
    commerceLoading,
    commerceSaving,
    commerceSaveSuccess,
    commerceSaveError,
    commerceSettings,
    lineOaOptions,
    storefrontFrontUrl,
    suggestedDefaultStorefrontUrl,
    applySuggestedStorefrontUrl,
    paymentProviders,
    invoiceProviders,
    logisticsProviders,
    newProvider,
    newProviderCustomCode,
    newProviderDisplayName,
    newProviderResolvedCode,
    newLogisticsProvider,
    newLogisticsProviderCustomCode,
    newLogisticsProviderDisplayName,
    newLogisticsProviderResolvedCode,
    newInvoiceProvider,
    invoiceProviderOptionsToAdd,
    isCatalogIntegrationProvider,
    integrationProviderRowLabel,
    integrationProviderChipLabel,
    COMMERCE_SETTINGS_SCOPE_KEYS,
    loadCommerceSettings,
    handleCommerceSave,
    isScopeDirty,
    isTabDirty,
    revertTab,
    captureSettingsBaseline,
    saveIntegrationSection,
    invoiceCarrierOn,
    toggleInvoiceCarrier,
    toggleOrderCaptureOnly,
    onProviderEnabledSwitch,
    handleAddProvider,
    handleDeleteProvider,
    paymentProviderDeleteTarget,
    paymentProviderDeleting,
    cancelDeletePaymentProvider,
    confirmDeletePaymentProvider,
    expandedProvider,
    providerKey,
    toggleProviderExpand,
    providerCredentials,
    providerCredSaving,
    providerCredSuccess,
    providerCredError,
    getProviderFields,
    setCredentialField,
    handleSaveCredentials,
    fillEcpaySandboxCredentials,
    fillEcpayLogisticsSandboxCredentials,
    fillEcpayInvoiceSandboxCredentials,
    onLogisticsProviderEnabledSwitch,
    handleAddLogisticsProvider,
    handleDeleteLogisticsProvider,
    onInvoiceProviderEnabledSwitch,
    handleAddInvoiceProvider,
    handleDeleteInvoiceProvider,
    isSecretVisible,
    toggleSecretVisible,
    newCredentialKey,
    addCredentialField,
    removeCredentialField,
    storefrontCategoriesResolved,
    storefrontTemplateFlatOptions,
    storefrontThemeOptionsForSelectedTemplate,
  }
}
